import { type UseModalState } from "@clipboard-health/ui-react";
import { Alert, Stack, TextField } from "@mui/material";
import { useEffect, useState } from "react";

import { Button } from "../../../components/Button";
import { Dialog } from "../../../components/Dialog";
import { LoadingButton } from "../../../components/LoadingButton";
import { Text } from "../../../components/Text";
import { useDeleteWorkerLock } from "../api/useDeleteWorkerLock";

interface UnlockConfirmationDialogProps {
  modalState: UseModalState;
  lockReason: string;
  unlockCondition: string;
  lockId: string;
  workplaceId: string;
}

export function UnlockConfirmationDialog(props: UnlockConfirmationDialogProps) {
  const { modalState, lockReason, unlockCondition, lockId, workplaceId } = props;
  const [error, setError] = useState<string | null>(null);

  const deleteWorkerLock = useDeleteWorkerLock();

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setError(null);
    }
  }, [modalState.modalIsOpen]);

  const handleUnlock = async () => {
    setError(null);
    try {
      await deleteWorkerLock.mutateAsync({ workplaceId, lockId });
      modalState.closeModal();
    } catch {
      setError("Failed to unlock this professional. Please try again.");
    }
  };

  return (
    <Dialog modalState={modalState} title="Unlock Professional">
      <Stack gap={3}>
        <Stack gap={1}>
          <Text semibold>Reason for Locking</Text>
          <TextField multiline disabled value={lockReason} />
        </Stack>

        <Stack gap={1}>
          <Text semibold>How to Unlock</Text>
          <TextField multiline disabled value={unlockCondition} />
        </Stack>

        <Text>Are you sure you want to unlock this professional?</Text>

        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
      <Stack direction="row" justifyContent="flex-end" gap={2} sx={{ marginTop: 4 }}>
        <Button variant="outlined" onClick={modalState.closeModal}>
          Cancel
        </Button>
        <LoadingButton
          isLoading={deleteWorkerLock.isLoading}
          variant="primary"
          onClick={handleUnlock}
        >
          Unlock
        </LoadingButton>
      </Stack>
    </Dialog>
  );
}
